"use client";

import "./consent-banner.css";
import { PRIVACY_POLICY_URL } from "@/lib/cookie-consent";
import { useTranslations } from "next-intl";
import Link from "next/link";
import {
  type KeyboardEvent,
  type ReactElement,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { ConsentCategories } from "./consent-categories";
import {
  type ConsentChoices,
  FULL_CONSENT,
  NO_CONSENT,
} from "./consent-record";

const SCROLL_LOCK_CLASS = "hvcc-scroll-lock";

/** Modal keeps Tab inside: no decision, no way past the banner. */
function trapTab(event: KeyboardEvent<HTMLDivElement>): void {
  if (event.key !== "Tab") return;
  const focusables = event.currentTarget.querySelectorAll<HTMLElement>(
    "button, a[href], input:not([disabled])",
  );
  const first: HTMLElement | undefined = focusables[0];
  const last: HTMLElement | undefined = focusables[focusables.length - 1];
  if (first === undefined || last === undefined) return;
  const current: Element | null = document.activeElement;
  if (
    event.shiftKey &&
    (current === first || current === event.currentTarget)
  ) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && current === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Reject and accept: same size, same layer; accept black, reject grey. No close X:
 * closing is no answer. Reopened banner starts on the switches.
 */
export function ConsentBanner({
  modal,
  choices,
  onSave,
}: {
  readonly modal: boolean;
  readonly choices: ConsentChoices | null;
  readonly onSave: (choices: ConsentChoices) => void;
}): ReactElement {
  const t = useTranslations("public.cookieConsent");
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId: string = useId();
  const bodyId: string = useId();
  const [details, setDetails] = useState<boolean>(choices !== null);
  const [draft, setDraft] = useState<ConsentChoices>(choices ?? NO_CONSENT);

  useEffect(() => {
    if (!modal) return;
    const root: HTMLElement = document.documentElement;
    root.classList.add(SCROLL_LOCK_CLASS);
    cardRef.current?.focus({ preventScroll: true });
    return () => {
      root.classList.remove(SCROLL_LOCK_CLASS);
    };
  }, [modal]);

  return (
    <div className={modal ? "hvcc-root hvcc-root--modal" : "hvcc-root"}>
      {modal ? <div className="hvcc-backdrop" aria-hidden="true" /> : null}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal={modal}
        aria-labelledby={titleId}
        aria-describedby={details ? undefined : bodyId}
        tabIndex={-1}
        className="hvcc-card"
        onKeyDown={modal ? trapTab : undefined}
      >
        <h2 id={titleId} className="hvcc-title">
          {details ? t("detailsTitle") : t("title")}
        </h2>
        {details ? (
          <ConsentCategories
            draft={draft}
            onToggle={(category, on) =>
              setDraft((current) => ({ ...current, [category]: on }))
            }
          />
        ) : (
          // First layer stays generic: never name vendors (Google, Meta). Vendors live in the switches layer.
          <p id={bodyId} className="hvcc-body">
            {t("body")}
          </p>
        )}
        <div className="hvcc-actions">
          <button
            type="button"
            className="hvcc-btn hvcc-btn--reject"
            onClick={() => onSave(NO_CONSENT)}
          >
            {t("rejectAll")}
          </button>
          <button
            type="button"
            className="hvcc-btn hvcc-btn--accept"
            onClick={() => onSave(details ? draft : FULL_CONSENT)}
          >
            {details ? t("save") : t("acceptAll")}
          </button>
        </div>
        <div className="hvcc-links">
          {details ? null : (
            <button
              type="button"
              className="hvcc-link-btn"
              onClick={() => setDetails(true)}
            >
              {t("customize")}
            </button>
          )}
          <Link href={PRIVACY_POLICY_URL}>{t("privacy")}</Link>
          <Link href="/impressum">{t("imprint")}</Link>
        </div>
      </div>
    </div>
  );
}
